import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as orderApi from '../api/order'

export interface OrdersState {
  orders: orderApi.Order[]
  loading: boolean
  pagination: {
    page: number
    pageSize: number
    total: number
  }
  error: string | null
}

const initialState: OrdersState = {
  orders: [],
  loading: false,
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
  },
  error: null,
}

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (
    { page, pageSize }: { page?: number; pageSize?: number } = {},
    { rejectWithValue },
  ) => {
    try {
      return await orderApi.fetchOrders(page || 1, pageSize || 20)
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch orders')
    }
  },
)

export const placeOrder = createAsyncThunk(
  'orders/placeOrder',
  async (
    orderDetails: Array<{ sku: string; quantity: number }>,
    { rejectWithValue },
  ) => {
    try {
      return await orderApi.createOrder(orderDetails)
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to place order')
    }
  },
)

export const editOrder = createAsyncThunk(
  'orders/editOrder',
  async (
    {
      id,
      data,
    }: {
      id: string
      data: Partial<{
        orderDetails: orderApi.OrderLineItem[]
        invoicedAt: string
        shippedAt: string
      }>
    },
    { rejectWithValue },
  ) => {
    try {
      return await orderApi.updateOrder(id, data)
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update order')
    }
  },
)

export const removeOrder = createAsyncThunk(
  'orders/removeOrder',
  async (id: string, { rejectWithValue }) => {
    try {
      await orderApi.deleteOrder(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete order')
    }
  },
)

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false
        state.orders = action.payload.results
        state.pagination = action.payload.pagination
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Place Order
      .addCase(placeOrder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.loading = false
        if (!state.orders) {
          state.orders = []
        }
        if (!state.pagination) {
          state.pagination = { page: 1, pageSize: 20, total: 0 }
        }
        state.orders.unshift(action.payload)
        state.pagination.total += 1
      })
      .addCase(placeOrder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Edit Order
      .addCase(editOrder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(editOrder.fulfilled, (state, action) => {
        state.loading = false
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id,
        )
        if (index >= 0) {
          state.orders[index] = action.payload
        }
      })
      .addCase(editOrder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Remove Order
      .addCase(removeOrder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeOrder.fulfilled, (state, action) => {
        state.loading = false
        state.orders = state.orders.filter(
          (order) => order.id !== action.payload,
        )
        state.pagination.total -= 1
      })
      .addCase(removeOrder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError } = ordersSlice.actions

export default ordersSlice.reducer
